import { Dropdown, DropdownComponents } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackObjectOpenCloseEvent } from '@/analytics/snowplow/analytics';
import { METRIC_KEY } from '@/consts/searchKeys';
import { ALL_EVENTS_METRIC_KEY } from '@/consts/visualisations';
import { useEnsureMetricKey } from '@/hooks/useEnsureMetricKey';
import { useSortObjectArray } from '@/hooks/useSortObjectArray';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { PromiseAtom } from '@/types/atom';
import { StringOption } from '@/types/generic';
import { MetricDefinition, MetricOptionData } from '@/types/metric';
import { snakeToCamel } from '@/utils/queries';
import { MetricThreeSixtyIcon } from '../icons/ThreeSixtyIcon';

const ALL_EVENTS_METRIC_KEYS = [ALL_EVENTS_METRIC_KEY];

type DropdownOptionProps = React.ComponentProps<typeof DropdownComponents.Option>;

type MetricOptionProps = Omit<DropdownOptionProps, 'data'> & {
  data: MetricOptionData;
};

// @todo Why is this not in the Component Library?
const MetricOption = ({ data, ...rest }: MetricOptionProps) => (
  <DropdownComponents.Option {...rest} data={data}>
    <div className="flex items-center justify-between gap-4" data-testid={data.value}>
      <span>{data.label}</span>
      {data.is360 && <MetricThreeSixtyIcon />}
    </div>
  </DropdownComponents.Option>
);

const MetricKeyDropdownBase = ({
  metricData = [],
  onMetricChange,
}: {
  metricData?: MetricDefinition[];
  onMetricChange?: (metricKey: string) => void;
}): JSX.Element => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['filters', 'metrics']);

  const navigate = useNavigate();
  const selectedMetricKey = searchParams.get(METRIC_KEY);
  const createQueryString = useUpdateQueryString(searchParams);

  const translatedOptions = metricData.map(metric => ({
    label: t(`${snakeToCamel(metric.key)}.name`, { ns: 'metrics' }),
    value: metric.key,
    is360: metric.is360,
  }));
  const sortedMetricOptions = useSortObjectArray('label', translatedOptions);
  const allEventsOption = {
    label: t(`${snakeToCamel(ALL_EVENTS_METRIC_KEY)}.name`, { ns: 'metrics' }),
    value: ALL_EVENTS_METRIC_KEY,
    is360: false,
  };
  const options = [allEventsOption, ...sortedMetricOptions];

  const onChange = (option: StringOption) => {
    if (onMetricChange) {
      onMetricChange(option.value);
    } else {
      navigate(`?${createQueryString([{ key: METRIC_KEY, value: option.value }])}`);
    }

    trackObjectOpenCloseEvent({
      objectType: 'Widget',
      openVsClose: 'Open',
      conditionType: option.value,
    });
  };

  return (
    <div className="min-w-[150px]">
      <Dropdown
        id="metric-key-dropdown"
        labelPosition="none"
        onChange={onChange}
        label={t('metricKey')}
        options={options}
        value={!selectedMetricKey ? null : options.find(({ value }) => value === selectedMetricKey)}
        variant="tertiary"
        components={{
          Option: MetricOption,
        }}
      />
    </div>
  );
};

const MetricKeyDropdownWithData = ({
  metricDataAtom,
  initialMetricKey,
  onMetricChange,
}: {
  metricDataAtom: PromiseAtom<MetricDefinition[]>;
  initialMetricKey?: string;
  onMetricChange?: (metricKey: string) => void;
}): JSX.Element => {
  const metricData = useAtomValue(metricDataAtom);
  useEnsureMetricKey(initialMetricKey, metricData, ALL_EVENTS_METRIC_KEYS);

  return <MetricKeyDropdownBase metricData={metricData} onMetricChange={onMetricChange} />;
};

export const MetricKeyDropdown = ({
  metricDataAtom,
  initialMetricKey,
  onMetricChange,
}: {
  metricDataAtom: PromiseAtom<MetricDefinition[]>;
  initialMetricKey?: string;
  onMetricChange?: (metricKey: string) => void;
}): JSX.Element => (
  <Suspense fallback={<MetricKeyDropdownBase />}>
    <MetricKeyDropdownWithData
      metricDataAtom={metricDataAtom}
      initialMetricKey={initialMetricKey}
      onMetricChange={onMetricChange}
    />
  </Suspense>
);
